import * as RadixSelect from "@radix-ui/react-select";
import cx from "classnames";
import {
	type ComponentType,
	type FC,
	type ForwardRefRenderFunction,
	type RefAttributes,
	forwardRef,
} from "react";

import styles from "./_.module.css";

import { CheckmarkIcon, ChevronDownIcon, ChevronUpIcon } from "@moment/icons";
import { Icon } from "@moment/ui";

export const InputSelectSizeOptions = ["sm", "md", "lg"] as const;
export type InputSelectSize = (typeof InputSelectSizeOptions)[number];

const InputSelectRoot: FC<
	RadixSelect.SelectProps & { size?: InputSelectSize; searchable?: boolean }
> = ({ children, size = "sm", ...props }) => {
	return (
		<RadixSelect.Root {...props}>
			<div
				className={cx(styles[`InputSelect`], styles[`InputSelect--${size}`], "group", {
					"[&>button]:h-8": size === "sm",
					"[&>button]:h-[38px]": size === "md",
					"[&>button]:h-[46px]": size === "lg",
				})}
			>
				{children}
			</div>
		</RadixSelect.Root>
	);
};

const InputSelectTrigger: ForwardRefRenderFunction<
	HTMLButtonElement,
	RadixSelect.SelectTriggerProps & RefAttributes<HTMLButtonElement> & { icon?: ComponentType }
> = ({ children, className, icon, ...props }, ref) => {
	return (
		<RadixSelect.Trigger
			ref={ref}
			className={cx([
				styles["InputSelectTrigger"],
				"w-full",
				"shadow-border-default",
				"bg-primary",
				"outline-none",
				"flex",
				"flex-row",
				"items-center",
				"overflow-hidden",
				className,
			])}
			{...props}
		>
			{icon && (
				<div className="mr-2">
					<Icon icon={icon} />
				</div>
			)}
			<div className="truncate">{children}</div>

			<InputSelect.Icon className="ml-auto pl-1">
				<ChevronDownIcon className="fill-icon-default group-hover:fill-icon-hover" />
			</InputSelect.Icon>
		</RadixSelect.Trigger>
	);
};

const InputSelectValue: ForwardRefRenderFunction<
	HTMLButtonElement,
	RadixSelect.SelectValueProps & RefAttributes<HTMLSpanElement>
> = ({ className, ...props }, ref) => {
	return <RadixSelect.Value ref={ref} className={cx(className)} {...props} />;
};

const InputSelectContent: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixSelect.SelectContentProps & RefAttributes<HTMLDivElement>
> = ({ children, ...props }, ref) => {
	return (
		<RadixSelect.Content
			asChild // Needed for ScrollUpButton and ScrollDownButton to show correctly
			{...props}
			ref={ref}
			className={cx([props.className, "z-popover", "rounded", "shadow-lg", "border"])}
		>
			<div className={cx("bg-primary-raised", "text-sm")}>{children}</div>
		</RadixSelect.Content>
	);
};

const InputSelectLabel: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixSelect.SelectLabelProps & RefAttributes<HTMLDivElement>
> = ({ children, ...props }, ref) => {
	return (
		<RadixSelect.Label
			{...props}
			ref={ref}
			className={`${props.className} flex flex-row py-2 pl-1 pr-4 text-tertiary`}
		>
			<div className="mr-2 w-4" />
			{children}
		</RadixSelect.Label>
	);
};

const InputSelectItem: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixSelect.SelectItemProps &
		RefAttributes<HTMLDivElement> & { variant?: "default" | "negative" }
> = ({ children, variant, ...props }, ref) => {
	return (
		<RadixSelect.Item
			{...props}
			ref={ref}
			className={cx([
				props.className,
				"flex cursor-pointer flex-row items-center space-x-2 p-2 text-primary",
				// IMPORTANT: Prevents bug where clicking on an element and then moving the mouse
				// causes things around the `InputSelect` to get highlighted. This is because the
				// selection is active when the menu closes.
				"select-none",
				// We are now using a blanket `transition-all`, but it is not very performant on
				// Radix components, so we're opting out.
				"!transition-none",
				styles["InputSelectItem"],
				variant === "negative" && styles["InputSelectItem--negative"],
			])}
		>
			<div className="flex items-center">
				<div className="mr-1 w-4">
					<InputSelect.ItemIndicator>
						<CheckmarkIcon size={16} />
					</InputSelect.ItemIndicator>
				</div>
				<InputSelect.ItemText>{children}</InputSelect.ItemText>
			</div>
		</RadixSelect.Item>
	);
};

const InputSelectSeparator: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixSelect.SelectSeparatorProps & RefAttributes<HTMLDivElement>
> = (props, ref) => {
	return <RadixSelect.Separator {...props} ref={ref} className="my-2 border-t" />;
};

const scrollButtonClasses = "flex justify-center py-1 hover:bg-tertiary cursor-pointer group";
const InputSelectScrollUpButton: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixSelect.SelectScrollUpButtonProps & RefAttributes<HTMLDivElement>
> = (props, ref) => {
	return (
		<RadixSelect.ScrollUpButton
			{...props}
			ref={ref}
			className={cx(scrollButtonClasses, "border-b", "rounded-t")}
		>
			<ChevronUpIcon className="group-hover:fill-icon-hover" size={16} />
		</RadixSelect.ScrollUpButton>
	);
};

const InputSelectScrollDownButton: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixSelect.SelectScrollDownButtonProps & RefAttributes<HTMLDivElement>
> = (props, ref) => {
	return (
		<RadixSelect.ScrollDownButton
			{...props}
			ref={ref}
			className={cx(scrollButtonClasses, "border-t", "rounded-b")}
		>
			<ChevronDownIcon className="group-hover:fill-icon-hover" size={16} />
		</RadixSelect.ScrollDownButton>
	);
};

const InputSelectViewport: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixSelect.SelectViewportProps & RefAttributes<HTMLDivElement>
> = ({ children, ...props }, ref) => {
	return (
		<RadixSelect.Viewport {...props} ref={ref} className="py-2">
			{children}
		</RadixSelect.Viewport>
	);
};

// TODO @shekhar create a BasicInputSelect component that covers most implementations
export const InputSelect = {
	Root: InputSelectRoot,
	Trigger: forwardRef(InputSelectTrigger),
	Value: forwardRef(InputSelectValue),
	Icon: RadixSelect.Icon,
	Portal: RadixSelect.Portal,
	Content: forwardRef(InputSelectContent),
	ScrollUpButton: forwardRef(InputSelectScrollUpButton),
	ScrollDownButton: forwardRef(InputSelectScrollDownButton),
	Viewport: forwardRef(InputSelectViewport),
	Item: forwardRef(InputSelectItem),
	ItemText: RadixSelect.ItemText,
	ItemIndicator: RadixSelect.ItemIndicator,
	Group: RadixSelect.Group,
	Label: forwardRef(InputSelectLabel),
	Separator: forwardRef(InputSelectSeparator),
};
